import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api, setCustomerHeader } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { setWorkflow } from "@/components/StepFields";

const CustomerContext = createContext(null);
const STORE = "kanex.customerId";

export function CustomerProvider({ children }) {
  const { user } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [canWorkAcross, setCanWorkAcross] = useState(false);
  const [customerId, setCustomerId] = useState(localStorage.getItem(STORE) || "");
  const [clock, setClock] = useState("ld");
  // the customer's stages live in state, so a screen that renders before the flow arrives
  // renders again the moment it does
  const [stages, setStages] = useState(null);
  const [ready, setReady] = useState(false);

  const pick = useCallback((id) => {
    setCustomerId(id);
    setCustomerHeader(id);
    localStorage.setItem(STORE, id || "");
  }, []);

  const load = useCallback(async () => {
    if (!user) return;
    try {
      // the list of my customers must not be asked for "as" a customer: a stale id left in
      // this browser would be refused and the picker would come back empty
      const { data } = await api.get("/customers", { headers: { "X-Customer-Id": null } });
      setCustomers(data.customers);
      setCanWorkAcross(data.canWorkAcross);
      const stored = localStorage.getItem(STORE) || "";
      const valid =
        stored === "__all" ? data.canWorkAcross : data.customers.some((c) => c.id === stored);
      pick(valid ? stored : data.customers[0]?.id || "");
    } catch {
      setCustomers([]);
    } finally {
      setReady(true);
    }
  }, [user, pick]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!user || !customerId) return;
    api
      .get("/meta")
      .then(({ data }) => {
        setWorkflow(data.workflow);
        setStages((data.workflow && data.workflow.stages) || []);
        setClock(data.clock || "ld");
      })
      .catch(() => {});
  }, [user, customerId]);

  const current = customers.find((c) => c.id === customerId) || null;

  return (
    <CustomerContext.Provider
      value={{
        customers,
        canWorkAcross,
        customerId,
        current,
        clock,
        onExpiryClock: clock === "expiry",
        stages: stages || [],
        stagesReady: stages !== null,
        isAllCustomers: customerId === "__all",
        pick,
        ready,
        reload: load,
        customerName: (id) => customers.find((c) => c.id === id)?.name || "",
      }}
    >
      {children}
    </CustomerContext.Provider>
  );
}

export function useCustomer() {
  return useContext(CustomerContext) || { customers: [], pick: () => {}, clock: "ld", onExpiryClock: false, stages: [], stagesReady: false };
}
